<template>
  <div class="page-container">
    <LoaderOverlay v-if="loading" />

    <EmptyState v-if="error && !loading" :error="error" />

    <div v-else-if="company" class="company-page">
      <!-- En-tête avec logo et infos principales -->
      <div class="company-header">
        <div class="company-logo">
          <img
            v-if="company.logoName"
            :src="`${config.uploadsUrl}/logos/${company.logoName}`"
            :alt="company.name"
          />
          <div v-else class="logo-placeholder">
            {{ company.name[0] }}
          </div>
        </div>

        <div class="company-main-info">
          <h1>{{ company.name }}</h1>
          <StarRating
            :rating="company.averageRating"
            :count="company.ratingsCount"
          />
          <div v-if="company.address" class="company-address">
            📍 {{ company.address.city?.name }}, {{ company.address.country?.name }}
          </div>
        </div>
      </div>

      <!-- Statistiques détaillées -->
      <div v-if="company.ratingsCount > 0" class="stats-section">
        <h2>Statistiques des avis</h2>
        <div class="card">
          <div class="card-content">
            <div class="card-item">
              <strong>Ponctualité</strong>
              <StarRating
                v-if="company.punctualityAverage"
                :rating="company.punctualityAverage"
                :count="0"
                :show-count="false"
              />
              <span v-else>—</span>
            </div>

            <div class="card-item">
              <strong>Confort</strong>
              <StarRating
                v-if="company.comfortAverage"
                :rating="company.comfortAverage"
                :count="0"
                :show-count="false"
              />
              <span v-else>—</span>
            </div>

            <div class="card-item">
              <strong>Propreté</strong>
              <StarRating
                v-if="company.cleanlinessAverage"
                :rating="company.cleanlinessAverage"
                :count="0"
                :show-count="false"
              />
              <span v-else>—</span>
            </div>

            <div class="card-item card-item--last">
              <strong>Rapport qualité/prix</strong>
              <StarRating
                v-if="company.valueForMoneyAverage"
                :rating="company.valueForMoneyAverage"
                :count="0"
                :show-count="false"
              />
              <span v-else>—</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Avis clients -->
      <div v-if="company.reviews.length > 0" class="reviews-section">
        <h2>Avis des voyageurs ({{ company.ratingsCount }})</h2>

        <div class="review-item card" v-for="review in company.reviews" :key="review.id">
          <div class="review-header">
            <div>
              <strong>{{ review.userName }}</strong>
              <StarRating
                :rating="review.overallRating"
                :count="0"
                :show-count="false"
              />
            </div>
            <span class="review-date">{{ formatDate(review.createdAt) }}</span>
          </div>

          <p v-if="review.reviewText" class="review-text">
            {{ review.reviewText }}
          </p>

          <div v-if="review.isVerified" class="verified-badge">
            ✓ Voyage vérifié
          </div>
        </div>
      </div>

      <div v-else class="no-reviews">
        <p>Aucun avis pour le moment.</p>
      </div>

      <button class="btn btn-secondary" @click="$router.back()">
        Retour
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import LoaderOverlay from '../components/LoaderOverlay.vue'
import EmptyState from '../components/EmptyState.vue'
import StarRating from '../components/StarRating.vue'
import { config } from '@/config/env.js'
import { useToast } from 'vue-toast-notification'

const route = useRoute()
const toast = useToast()
const company = ref(null)
const loading = ref(true)
const error = ref(null)

onMounted(async () => {
  try {
    const response = await fetch(`${config.apiUrl}/company/${route.params.slug}`)

    if (!response.ok) {
      throw new Error('Compagnie introuvable')
    }

    company.value = await response.json()
  } catch (err) {
    error.value = err.message
    toast.error(err.message)
  } finally {
    loading.value = false
  }
})

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}
</script>

<style scoped>
.card {
  border: 2px solid var(--color-border);
  overflow: hidden;
  padding: 0;
}

.company-page {
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.company-header {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  padding: 2rem 0;
}

.company-logo {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.company-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius-md);
  border: 2px solid var(--color-border);
}

.logo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--color-jungle), var(--color-feuille));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: bold;
  border-radius: var(--border-radius-md);
}

.company-main-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.company-main-info h1 {
  margin: 0;
  font-size: 2rem;
}

.company-address {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

.stats-section h2,
.reviews-section h2 {
  font-size: 1.3rem;
  margin-bottom: 1rem;
}

.review-item {
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.review-item:last-child {
  margin-bottom: 0;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.review-header > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.review-date {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.review-text {
  margin: 0 0 0.75rem 0;
  line-height: 1.6;
}

.verified-badge {
  display: inline-block;
  background: var(--color-pastel-green);
  color: var(--color-jungle);
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.875rem;
  font-weight: 600;
}

.no-reviews {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-secondary);
}

@media (max-width: 768px) {
  .company-header {
    flex-direction: column;
    text-align: center;
  }

  .review-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
